import { useMemo } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input, Select } from '@/components/ui/input'
import type { AdminColumn, FilterValue } from './types'

/**
 * Filtr paneli — ustun TURIGA qarab avtomatik boshqaruv.
 * Ekran filtr UI'sini yozmaydi; ustun turini e'lon qiladi (X1).
 */

function datePreset(preset: string): [string, string] | null {
  const now = new Date()
  const iso = (d: Date) => d.toISOString().slice(0, 10)
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  switch (preset) {
    case 'today':
      return [iso(startOfDay), iso(now)]
    case 'last7':
      return [iso(new Date(startOfDay.getTime() - 6 * 864e5)), iso(now)]
    case 'last30':
      return [iso(new Date(startOfDay.getTime() - 29 * 864e5)), iso(now)]
    case 'thisMonth':
      return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)]
    case 'lastMonth': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const last = new Date(now.getFullYear(), now.getMonth(), 0)
      return [iso(first), iso(last)]
    }
    default:
      return null
  }
}

function FilterEditor<T>({
  col,
  value,
  onChange,
}: {
  col: AdminColumn<T>
  value: FilterValue | undefined
  onChange: (v: FilterValue | null) => void
}) {
  const t = useT()

  switch (col.type) {
    case 'enum':
    case 'tags': {
      const selected = new Set(
        Array.isArray(value?.value) ? value.value.map(String) : value ? [String(value.value)] : [],
      )
      return (
        <div className="flex flex-wrap gap-1.5">
          {col.options?.map((o) => {
            const on = selected.has(o.value)
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  const next = new Set(selected)
                  if (on) next.delete(o.value)
                  else next.add(o.value)
                  onChange(next.size ? { key: col.key, op: 'in', value: [...next] } : null)
                }}
                className={cn(
                  'rounded-sm border px-2 py-0.5 text-sm transition-colors',
                  on
                    ? 'border-accent bg-accent-subtle text-accent'
                    : 'border-border-default text-secondary hover:border-border-strong hover:text-primary',
                )}
              >
                {o.label}
              </button>
            )
          })}
        </div>
      )
    }

    case 'bool': {
      const v = value?.value
      return (
        <Select
          value={v === undefined ? '' : String(v)}
          onChange={(e) => {
            const raw = (e.target as HTMLSelectElement).value
            onChange(raw === '' ? null : { key: col.key, op: 'is', value: raw === 'true' })
          }}
        >
          <option value="">{t.common.all}</option>
          <option value="true">{t.common.yes}</option>
          <option value="false">{t.common.no}</option>
        </Select>
      )
    }

    case 'date': {
      const [from = '', to = ''] = Array.isArray(value?.value) ? value.value.map(String) : []
      const setRange = (f: string, tt: string) =>
        onChange(f || tt ? { key: col.key, op: 'between', value: [f, tt] } : null)
      return (
        <div className="space-y-1.5">
          <div className="flex flex-wrap gap-1">
            {(['today', 'last7', 'last30', 'thisMonth', 'lastMonth'] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => {
                  const r = datePreset(p)
                  if (r) setRange(r[0], r[1])
                }}
                className="rounded-sm border border-border-default px-1.5 py-0.5 text-xs text-secondary hover:border-border-strong hover:text-primary"
              >
                {t.filter[p]}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <Input type="date" value={from} onChange={(e) => setRange(e.target.value, to)} className="text-sm" />
            <span className="text-xs text-tertiary">→</span>
            <Input type="date" value={to} onChange={(e) => setRange(from, e.target.value)} className="text-sm" />
          </div>
        </div>
      )
    }

    case 'number':
    case 'money':
    case 'risk': {
      const [from = '', to = ''] = Array.isArray(value?.value) ? value.value.map(String) : []
      const setRange = (f: string, tt: string) =>
        onChange(f || tt ? { key: col.key, op: 'between', value: [f, tt] } : null)
      return (
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            placeholder={t.filter.from}
            value={from}
            onChange={(e) => setRange(e.target.value, to)}
          />
          <span className="text-xs text-tertiary">→</span>
          <Input
            type="number"
            placeholder={t.filter.to}
            value={to}
            onChange={(e) => setRange(from, e.target.value)}
          />
        </div>
      )
    }

    case 'id':
      return (
        <Input
          mono
          placeholder={t.filter.equals}
          value={value ? String(value.value) : ''}
          onChange={(e) =>
            onChange(e.target.value ? { key: col.key, op: 'equals', value: e.target.value } : null)
          }
        />
      )

    default:
      return (
        <Input
          placeholder={t.filter.contains}
          value={value ? String(value.value) : ''}
          onChange={(e) =>
            onChange(e.target.value ? { key: col.key, op: 'contains', value: e.target.value } : null)
          }
        />
      )
  }
}

export function FilterPanel<T>({
  columns,
  filters,
  onChange,
  open,
}: {
  columns: AdminColumn<T>[]
  filters: FilterValue[]
  onChange: (next: FilterValue[]) => void
  open: boolean
}) {
  const filterable = useMemo(() => columns.filter((c) => c.filterable !== false), [columns])
  const byKey = useMemo(() => new Map(filters.map((f) => [f.key, f])), [filters])

  function setFilter(key: string, v: FilterValue | null) {
    const next = filters.filter((f) => f.key !== key)
    if (v) next.push(v)
    onChange(next)
  }

  if (!open) return null

  return (
    <div className="grid gap-4 border-b border-border-subtle bg-surface-2 px-3 py-3 sm:grid-cols-2 lg:grid-cols-4">
      {filterable.map((col) => (
        <div key={col.key} className="space-y-1">
          <label className="block text-xs font-medium text-tertiary uppercase">{col.header}</label>
          <FilterEditor col={col} value={byKey.get(col.key)} onChange={(v) => setFilter(col.key, v)} />
        </div>
      ))}
    </div>
  )
}

/** Aktiv filtrlar — sarlavha ostida chip sifatida, har birida ✕ (tables.md). */
export function FilterChips<T>({
  columns,
  filters,
  onChange,
}: {
  columns: AdminColumn<T>[]
  filters: FilterValue[]
  onChange: (next: FilterValue[]) => void
}) {
  const t = useT()
  if (filters.length === 0) return null

  const label = (f: FilterValue): string => {
    const col = columns.find((c) => c.key === f.key)
    const head = col?.header ?? f.key
    if (f.op === 'between' && Array.isArray(f.value)) {
      const [a, b] = f.value
      return `${head}: ${a || '…'} → ${b || '…'}`
    }
    if (f.op === 'in' && Array.isArray(f.value)) {
      const names = f.value.map((v) => col?.options?.find((o) => o.value === String(v))?.label ?? String(v))
      return `${head}: ${names.join(', ')}`
    }
    if (f.op === 'is') return `${head}: ${f.value ? t.common.yes : t.common.no}`
    return `${head}: ${String(f.value)}`
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-3 py-2">
      {filters.map((f) => (
        <span
          key={`${f.key}:${f.op}`}
          className="inline-flex items-center gap-1 rounded-sm border border-border-default bg-surface-2 py-0.5 pr-0.5 pl-2 text-sm text-secondary"
        >
          {label(f)}
          <button
            type="button"
            aria-label={t.common.clear}
            onClick={() => onChange(filters.filter((x) => x.key !== f.key))}
            className="inline-flex h-4 w-4 items-center justify-center rounded-sm hover:bg-surface-3 hover:text-primary"
          >
            <X size={11} />
          </button>
        </span>
      ))}
      {/* tables.md: «Hammasini tozalash» — 2+ filtr bo'lganda */}
      {filters.length >= 2 && (
        <Button variant="ghost" size="sm" onClick={() => onChange([])}>
          {t.table.clearFilters}
        </Button>
      )}
    </div>
  )
}
